import { useEffect } from 'react';
import { acquirePinchGuard, releasePinchGuard } from '../zoom-lock';

/**
 * Bulletproof page-scroll + zoom lock for open modals/overlays.
 *
 * `body { overflow: hidden }` alone is silently ignored by iOS Safari, so an
 * open modal still lets the page behind it scroll on a phone. The reliable
 * cross-browser fix is to pin the body with `position: fixed` offset by the
 * current scroll position, then restore that position on release.
 *
 * While a lock is held the page must also stay put horizontally: pinch-zoom
 * would let the visual viewport pan left/right under a full-screen modal, so
 * the lock holds the shared pinch guard (zoom-lock.ts) for its lifetime.
 * Single-finger scrolling inside the modal is untouched, and on a touch device
 * the app already holds that guard document-wide — this hold keeps the
 * behaviour on any browser the app-wide lock skips.
 * (Double-tap zoom is handled in CSS via `touch-action: manipulation` — see
 * App.css.)
 *
 * A module-level ref count keeps nested/stacked modals correct: the pin is
 * applied once when the first lock engages and lifted only when the last one
 * releases, so closing an inner modal never unpins the page under an outer one.
 */
let lockCount = 0;
let saved: {
  scrollY: number;
  position: string;
  top: string;
  left: string;
  right: string;
  width: string;
  overflow: string;
  htmlOverflow: string;
} | null = null;

function engageLock(): void {
  const scrollY = window.scrollY;
  const body = document.body;
  saved = {
    scrollY,
    position: body.style.position,
    top: body.style.top,
    left: body.style.left,
    right: body.style.right,
    width: body.style.width,
    overflow: body.style.overflow,
    htmlOverflow: document.documentElement.style.overflow,
  };
  body.style.position = 'fixed';
  body.style.top = `-${scrollY}px`;
  body.style.left = '0';
  body.style.right = '0';
  body.style.width = '100%';
  body.style.overflow = 'hidden';
  document.documentElement.style.overflow = 'hidden';
  acquirePinchGuard();
}

function releaseLock(): void {
  if (!saved) return;
  const body = document.body;
  const { scrollY } = saved;
  body.style.position = saved.position;
  body.style.top = saved.top;
  body.style.left = saved.left;
  body.style.right = saved.right;
  body.style.width = saved.width;
  body.style.overflow = saved.overflow;
  document.documentElement.style.overflow = saved.htmlOverflow;
  releasePinchGuard();
  saved = null;
  // Restore the scroll position the pin discarded
  window.scrollTo(0, scrollY);
}

/**
 * Locks page scroll while `active` is true, releasing on false/unmount.
 * Safe to call from every modal — the shared ref count handles stacking.
 */
export function useBodyScrollLock(active: boolean): void {
  useEffect(() => {
    if (!active) return;
    lockCount += 1;
    if (lockCount === 1) engageLock();
    return () => {
      lockCount = Math.max(0, lockCount - 1);
      if (lockCount === 0) releaseLock();
    };
  }, [active]);
}
